import { CSSProperties, FunctionComponent, HTMLAttributes, ReactNode } from 'react';

type PaperVariant = 'elevation' | 'outlined';

type PaperUIProps = {
  elevation?: number,
  variant?: PaperVariant,
  square?: boolean,
  children?: ReactNode,
} & HTMLAttributes<HTMLDivElement>;

/**
 * Surface.
 *
 * Prop API unchanged (`elevation` 0–24, `variant`, `square`), but the look is
 * now the design system's `.ds-card`: a themed surface with a hairline border
 * and a restrained shadow, instead of MUI's 25-step shadow ramp.
 *
 * `elevation` is collapsed onto the system's three shadow tokens. MUI's ramp
 * was far too fine-grained to be meaningful — in practice call sites used a
 * handful of values, and 25 distinct shadows is not something a design system
 * should try to preserve. 0 stays flat so `elevation={0}` call sites are
 * unaffected.
 */
const shadowFor = (elevation: number): string => {
  if (elevation <= 0) return 'none';
  if (elevation <= 2) return 'var(--ds-shadow-1)';
  if (elevation <= 8) return 'var(--ds-shadow-2)';
  return 'var(--ds-shadow-3)';
};

const PaperUI: FunctionComponent<PaperUIProps> = ({
  elevation = 1,
  variant = 'elevation',
  square = false,
  className = '',
  style,
  children,
  ...rest
}) => {
  const baseStyle: CSSProperties = {
    boxShadow: variant === 'outlined' ? 'none' : shadowFor(elevation),
    ...(square ? { borderRadius: 0 } : null),
    // Call-site style wins over the base, matching the previous behaviour.
    ...style,
  };

  return (
    <div className={`ds-card ${className}`} style={baseStyle} {...rest}>
      {children}
    </div>
  );
};

export default PaperUI;
